import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ─── ResizeObserver patch ─────────────────────────────────────────────
// React Flow observes its container size; under React StrictMode in dev,
// observer callbacks may dispatch synchronously and trigger the benign
// "ResizeObserver loop completed with undelivered notifications" event,
// which CRA's react-error-overlay surfaces as a full-screen red modal.
//
// The canonical fix is to wrap the callback in requestAnimationFrame so
// notifications are delivered on the next frame and never loop. This
// removes the warning at its source — no error handler workarounds needed.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeRO = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends NativeRO {
    constructor(callback) {
      const wrapped = (entries, observer) => {
        window.requestAnimationFrame(() => {
          try { callback(entries, observer); } catch (e) {
            if (!String(e?.message || "").includes("ResizeObserver")) throw e;
          }
        });
      };
      super(wrapped);
    }
  };
}

// Belt-and-braces: also swallow the event if it does slip through (e.g. native
// observer in extensions, jsdom, etc.) — capture phase + stopImmediatePropagation.
const ROB_PATTERNS = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
];
const isRobError = (msg) => typeof msg === "string" && ROB_PATTERNS.some((p) => msg.includes(p));
window.addEventListener("error", (e) => {
  if (isRobError(e.message)) { e.stopImmediatePropagation(); e.preventDefault(); return false; }
}, true);
window.addEventListener("unhandledrejection", (e) => {
  const m = e?.reason?.message || String(e?.reason || "");
  if (isRobError(m)) { e.stopImmediatePropagation(); e.preventDefault(); }
}, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
